import { isShibUser } from '@thro-helpers/users';
import { BillingPermissions } from '@thro-types/access-handler';
import {
  CreateInvoiceArticle,
  Customer,
  Invoice,
  InvoiceArticle,
  PatchInvoiceArticle,
  Tax
} from '@thro-types/billing';
import { User } from '@thro-types/users';

import {
  PDFGeneratorCompanyData,
  PDFGeneratorData,
  PDFGeneratorInvoiceData,
  PDFGeneratorOffer,
  TaxTotalWithTax
} from 'api/hooks/billing/pdfTypes';

export const DATEFORMATWITHTIME = 'dd.MM.yyyy, HH:mm';
export const DATEFORMAT = 'dd.MM.yyyy';

/**
 * Checks if a user has the necessary entitlements to create invoices.
 * @param {Object} user - The user object.
 * @param {string[]} userPermissionData - Array of permissions from user
 * @returns {Boolean} Whether the user is a finance user.
 */
export function canCreateInvoices(user: User, userPermissionData: string[]): boolean {
  return (
    user &&
    isShibUser(user) &&
    userPermissionData?.includes(BillingPermissions.BILLING_INVOICE_WRITE)
  );
}

/**
 * Checks if a user has the necessary entitlements to be considered a finance user.
 * @param {Object} user - The user object.
 * @param {string[]} userPermissionData - Array of permissions from user
 * @returns {Boolean} Whether the user is a finance user.
 */
export function isFinanceUser(user: User, userPermissionData: string[]): boolean {
  return (
    user &&
    isShibUser(user) &&
    userPermissionData?.includes(BillingPermissions.BILLING_FINANCE_WRITE)
  );
}

/**
 * Checks if a user is creator of invoice
 *
 * @param {Object} user - The user object.
 * @param {Object} invoice - The invoice object.
 * @returns {Boolean} Whether the user is the creator
 */
export function isCreatorOfInvoice(user: User, invoice: Invoice): boolean {
  return user && isShibUser(user) && user.uid === invoice.creator.uid;
}

/**
 * Checks if a user has permission to edit an invoice.
 *
 * @param {Object} user - The user object.
 * @param {Object} invoice - The invoice object.
 * @param {Boolean} financeUser - Whether the user is a member of the finance department.
 * @returns {Boolean} Whether the user can edit the invoice.
 */
export function canEditInvoice(
  user: User,
  invoice: Invoice,
  userPermissionData: string[]
): boolean {
  return (
    (isCreatorOfInvoice(user, invoice) && invoice.state === 'DRAFT') ||
    (isFinanceUser(user, userPermissionData) && invoice.state === 'READY')
  );
}

export function shouldRenderActionButtons(
  user: User,
  invoice: Invoice,
  userPermissionData: string[]
): boolean {
  return isCreatorOfInvoice(user, invoice) || isFinanceUser(user, userPermissionData);
}

/**
 * Checks if an invoice contains any free text article
 * @param {Object} invoice - The invoice object.
 * @returns {Boolean} Whether the invoice contains any free text article
 * */
export function hasFreeTextArticle(invoice: Invoice): boolean {
  return invoice.invoiceArticles.some(isFreeTextArticle);
}

/**
 * Checks if an article is a free text article
 * @param {Object} article - The article object.
 * @returns {Boolean} Whether the article is a free text article
 * */
export function isFreeTextArticle(article: InvoiceArticle): boolean {
  return !article.article;
}

/**
 * Checks if an invoice contains different tax versions
 * @param {Object} invoice - The invoice object.
 * @returns {Boolean} Whether the invoice contains different tax versions
 * */
export function hasDifferentTaxVersions(invoice: Invoice): boolean {
  return invoice.invoiceArticles.some(
    article => article.salestaxVersionId !== invoice.invoiceArticles[0].salestaxVersionId
  );
}

/**
 * Checks if an invoice contains articles with changed default tax
 * @param {Object} invoice - The invoice object.
 * @returns {Boolean} Whether the invoice contains articles with changed default tax
 * */
export function hasArticleWithChangedDefaultTax(invoice: Invoice): boolean {
  return invoice.invoiceArticles.some(article => hasChangedDefaultTax(article));
}

/**
 * Checks if an invoice has articles whose tax is not 0%
 * @param {Object} invoice - The invoice object.
 * @returns {Boolean} Whether the invoice has articles whose tax is not 0%
 * */
export function hasArticleWithTaxNotZero(invoice: Invoice): boolean {
  return invoice.invoiceArticles.some(article => hasTaxNotZero(article));
}

/**
 * Checks if an article has changed its default tax
 * @param {Object} article - The article object.
 * @returns {Boolean} Whether the article has changed its default tax
 * */
export function hasChangedDefaultTax(article: InvoiceArticle): boolean {
  if (!article.article?.defaultTaxId) return false;
  return article.article?.defaultTaxId !== article.salestaxVersionId;
}

/**
 * Checks if a customer is from germany
 * @param {Object} customer - The customer object.
 * @returns {Boolean} Whether the customer is german
 * */
export function isForeignCustomer(customer: Customer): boolean {
  if (customer.country !== 'DE') return true;
  return false;
}

/**
 * Checks if a customer is from germany
 * @param {Object} article - The customer object.
 * @returns {Boolean} Whether the customer is german
 * */
export function hasTaxNotZero(article: InvoiceArticle): boolean {
  if (article.salestax?.taxRate === 0) return false;
  return true;
}

/**
 * Gets the tax rates of all articles in an invoice and returns them as a string
 * @param {Object} invoice - The invoice object.
 * @returns {String[]} The tax rates of all articles with a % and a comma
 * */
export function getDistinctTaxRates(invoice: Invoice): string[] {
  const taxRates = new Set<string>();
  for (const article of invoice.invoiceArticles) {
    taxRates.add(`${article.salestax?.taxRate}%`);
  }
  return Array.from(taxRates);
}

/**
 * Gets the tax rates of all articles in an invoice and returns them as a string
 * @param {Object} invoice - The invoice object.
 * @returns {Tax[]} The tax rates of all articles with a % and a comma
 * */
export function getDistinctTaxes(invoice: Invoice): Tax[] {
  const taxRates = new Map<string, Tax>();
  for (const article of invoice.invoiceArticles) {
    if (!article.salestax) continue;
    const key = JSON.stringify(article.salestax);
    taxRates.set(key, article.salestax);
  }
  return Array.from(taxRates.values());
}

/**
 * Checks if an invoice is a cancelled invoice
 * @param {Object} invoice - The invoice object.
 * @returns {Boolean} Whether the invoice is a cancelled invoice
 */
export function isCancelledInvoice(invoice: Invoice): boolean {
  return !!invoice?.originalInvoice?.id;
}

export const getInvoicePdfData = (invoice: Invoice): PDFGeneratorData => {
  const companyName = invoice.customer?.companyName ?? '';
  const company: PDFGeneratorCompanyData = {
    name: companyName,
    zip: `${invoice.customer?.zip || '{Kunde_PLZ}'}`,
    city: `${invoice.customer?.city || '{Kunde_Stadt}'}`,
    country: `${invoice.customer?.country || '{Kunde_Land}'}`,
    street: `${invoice.customer?.address || '{Kunde_Straße}'}`,
    invoiceEmail: `${invoice.customer?.email || '{Kunde_Email}'}`,
    ustId: `${invoice.customer?.ustId || '{Kunde_USTID}'}`,
    sameInvoiceaddress: false
  };
  const taxTotals: TaxTotalWithTax[] = [];

  invoice.taxTotals.forEach(tax => {
    if (tax.tax)
      taxTotals.push({
        taxTotal: { ...tax, totalTax: tax.totalTax },
        tax: { ...tax.tax }
      });
  });
  const invoiceData: PDFGeneratorInvoiceData = {
    ksnr: '{Rechnungsnummer}',
    snr: '{BKZ}',
    title: `${invoice.title || '{Rechnung_Titel}'}`,
    totalNetAmount: invoice.totalNet || 0,
    taxTotals: taxTotals || [],
    totalGross: invoice.totalGross || 0,
    expertContactEmail: `${invoice.expertContact?.email || '{Ansprechpartner_Email}'}`,
    invoiceDate: invoice.invoiceDate,
    paymentTarget: invoice.paymentTarget || '0d',
    bankAccount: invoice.bankAccount,
    costBearer: invoice.costBearer || '{Kostenträger}',
    shorthand: invoice.yourReference || undefined,
    servicePeriodStart: invoice.servicePeriodStart || undefined,
    servicePeriodEnd: invoice.servicePeriodEnd || undefined,
    department: {
      name: invoice.department?.name || 'Abteilung Finanzen',
      email: invoice.department?.email || 'buchhaltung@th-rosenheim.de', //be careful, this default value is also used in billing service
      phone: invoice.department?.phone || '+49 (0)8031 805-0' //be careful - this default value is also used in billing service
    },
    isCancelledInvoice: invoice.originalInvoice?.id ? true : false
  };

  const defaultTax: Tax = {
    id: '',
    key: '',
    text: '',
    taxRate: 0,
    isCurrentVersion: false,
    createdAt: new Date(),
    updatedAt: new Date()
  };

  const offers: PDFGeneratorOffer[] = [];
  //Sort invoiceArticle by createdAt
  invoice.invoiceArticles.sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();

    return dateA - dateB;
  });
  invoice.invoiceArticles.forEach(article =>
    offers.push({
      title: `${article.name || '{Artikel_Name}'}`,
      description: `${article.description || '{Artikel_Beschreibung}'}`,
      amount: article.amount,
      tax: article.salestax || defaultTax,
      totalNetAmount: article.totalArticleNetAmount || 0,
      totalSalesTax: article.totalArticleSalesTax || 0,
      totalGross: article.totalArticleGross || 0,
      price: article.price,
      unit: article.unit
    })
  );

  const pdfData: PDFGeneratorData = {
    company: company,
    contactPerson: `${invoice.customer?.firstName || '{Kunde_Vorname}'} ${
      invoice.customer?.lastName || '{Kunde_Nachname}'
    }`,
    invoiceData: invoiceData,
    offers: offers
  };
  return pdfData;
};

export function shouldUpdateOrSplitSalestaxes(
  invoice: Invoice,
  updateArticle: PatchInvoiceArticle,
  article: InvoiceArticle
): boolean {
  return (
    invoice.state === 'READY' &&
    updateArticle.salestaxVersionId !== article.salestaxVersionId &&
    invoice.invoiceArticles.length > 1
  );
}

export function shouldUpdateOrSplitSalestaxesCreateInvoiceArticle(
  invoice: Invoice,
  article: CreateInvoiceArticle
): boolean {
  return (
    invoice.state === 'READY' &&
    invoice.invoiceArticles[0]?.salestaxVersionId !== article.salestaxVersionId &&
    invoice.invoiceArticles.length > 0
  );
}
